import { Box, Stack, Typography } from '@mui/material'
import { GRAY_900, WHITE } from 'constants/styling/theme'

import { CheckCircleOutline } from '@mui/icons-material'
import { MUITooltip } from 'components/common/MUITooltip'
import React from 'react'
import { WallColorPickerProps } from 'models/visualStaging/virtualStagingTypes'
import { useTranslation } from 'react-i18next'

/**
 * Wall color options with their hex values
 * Based on Propershot docs, we selected with OPS: bright white, beige, light grey, dark grey, light blue, light green, dark orange
 * apiName is the name of the color as it is used in the API from Propershot
 */
export const WALL_COLORS = [
  { id: 'bright_white', hex: '#f0f0f0', nameKey: 'virtual_staging.wall_colors.bright_white', checkmarkColor: GRAY_900, apiName: 'bright white' },
  { id: 'beige', hex: '#b5966e', nameKey: 'virtual_staging.wall_colors.beige', checkmarkColor: WHITE, apiName: 'beige' },
  { id: 'light_grey', hex: '#C6C0B8', nameKey: 'virtual_staging.wall_colors.light_grey', checkmarkColor: WHITE, apiName: 'light grey' },
  { id: 'dark_grey', hex: '#6e675d', nameKey: 'virtual_staging.wall_colors.dark_grey', checkmarkColor: WHITE, apiName: 'dark grey' },
  { id: 'light_blue', hex: '#9FB2B6', nameKey: 'virtual_staging.wall_colors.light_blue', checkmarkColor: WHITE, apiName: 'light blue' },
  { id: 'light_green', hex: '#77a380', nameKey: 'virtual_staging.wall_colors.light_green', checkmarkColor: WHITE, apiName: 'light green' },
  { id: 'dark_orange', hex: '#875e0e', nameKey: 'virtual_staging.wall_colors.dark_orange', checkmarkColor: WHITE, apiName: 'dark orange' },
] as const

/**
 * WallColorPicker component for Virtual Staging.
 * Displays a grid of color options for wall painting.
 * First color (bright white) is preselected by default.
 * 
 * @example
 * <WallColorPicker
 *   selectedColor={virtualStagingState.wallColor}
 *   onColorChange={handleColorChange}
 *   isDisabled={virtualStagingState.isGeneratingStaging}
 * />
 */
export const WallColorPicker: React.FC<WallColorPickerProps> = ({
  selectedColor,
  onColorChange,
  isDisabled = false,
}) => {
  const { t } = useTranslation(['photo_studio_editing'])

  // Default to first color if none selected
  const effectiveSelectedColor = selectedColor || WALL_COLORS[0].id

  const handleColorClick = (colorId: string) => {
    if (!isDisabled) {
      onColorChange(colorId)
    }
  }

  // Helper function to capitalize first letter
  const capitalizeFirstLetter = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1)
  }

  return (
    <Stack gap={0.8}>
      <Typography
        variant="text-md"
        sx={{
          fontWeight: 550,
          color: GRAY_900,
          lineHeight: 1.5,
          letterSpacing: '-0.64px',
        }}
      >
        {t('virtual_staging.wall_colors.title')}
      </Typography>
      <Box
        sx={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '0.8rem',
        }}
      >
        {WALL_COLORS.map((color) => {
          const isSelected = effectiveSelectedColor === color.id
          const colorName = capitalizeFirstLetter(t(color.nameKey))
          return (
            <MUITooltip
              key={color.id}
              content={colorName}
              placement="top"
              enterDelay={300}
            >
              <Box
                onClick={() => handleColorClick(color.id)}
                sx={{
                  position: 'relative',
                  width: '3.17rem',
                  height: '3.2rem',
                  borderRadius: '0.8rem',
                  backgroundColor: color.hex,
                  border: `1px solid ${isSelected ? GRAY_900 : 'transparent'}`,
                  cursor: isDisabled ? 'not-allowed' : 'pointer',
                  opacity: isDisabled ? 0.6 : 1,
                  transition: 'all 0.2s ease-in-out',
                  '&:hover': !isDisabled ? {
                    borderColor: GRAY_900,
                  } : {},
                }}
              >
                {isSelected && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: '50%',
                      top: '50%',
                      transform: 'translate(-50%, -50%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      width: '1.6rem',
                      height: '1.6rem',
                    }}
                  >
                    <CheckCircleOutline
                      sx={{
                        fontSize: '1.6rem',
                        width: '1.6rem',
                        height: '1.6rem',
                        color: color.checkmarkColor,
                      }}
                    />
                  </Box>
                )}
              </Box>
            </MUITooltip>
          )
        })}
      </Box>
    </Stack>
  )
}
